import React, { useState, useMemo, useRef } from 'react';
import { Sun, Download, Palette, Printer, RotateCcw } from 'lucide-react';
// ArrowLeftRight, ArrowUpDown - unused while orientation toggle is hidden
import { useCutList } from '../../contexts/CutListContext';
import { useAuth, FEATURES } from '../../contexts/AuthContext';
import { useWakeLock } from '../../hooks/useWakeLock';
import SheetRenderer from '../../shared/SheetRenderer';
import { getPartColor } from '../../utils/colors';
import { formatPreciseDimension, UNIT_SYSTEMS } from '../../utils/units';
import { orderCutsForFabrication } from '../../utils/cutOrderOptimizer';
import { printShopSheet, downloadShopSheet, downloadShopSheetPDF } from '../../utils/printShopSheet';

/**
 * Step 4: Execution - Shop sheet view for cutting
 * TODO: Guided cut mode is disabled for now, restore later
 */
const ExecutionStep = () => {
  const { layouts, currentUnits, sheetWidth, sheetLength, cutWidth } = useCutList();
  const { hasFeature, isPro, license, promptUpgrade } = useAuth();
  const { isLocked, isSupported, toggleWakeLock } = useWakeLock();
  const showWatermark = !hasFeature(FEATURES.CLEAN_EXPORT);

  const [printFriendly, setPrintFriendly] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [orientation, setOrientation] = useState('auto'); // 'auto', 'landscape', 'portrait'
  const [pdfExportStatus, setPdfExportStatus] = useState(null); // null | 'generating' | 'downloading' | 'complete' | 'error'

  // Track which pieces have been cut (checked off)
  const [cutPieceIds, setCutPieceIds] = useState(new Set());

  // Get ordered layouts with optimized cut sequence
  const orderedLayouts = useMemo(
    () => orderCutsForFabrication(layouts, cutWidth),
    [layouts, cutWidth]
  );

  // Snapshot layouts on mount for export (never affected by interactivity)
  const exportLayoutsRef = useRef(null);
  if (exportLayoutsRef.current === null) {
    exportLayoutsRef.current = orderedLayouts;
  }

  // Toggle a piece's cut status
  const togglePieceCut = (pieceId) => {
    setCutPieceIds(prev => {
      const next = new Set(prev);
      if (next.has(pieceId)) {
        next.delete(pieceId);
      } else {
        next.add(pieceId);
      }
      return next;
    });
  };

  // Toggle all pieces in a part group (by matching name+dims+grain)
  const togglePartGroupCut = (partKey, pieceIds) => {
    setCutPieceIds(prev => {
      const next = new Set(prev);
      const allCut = pieceIds.every(id => prev.has(id));
      if (allCut) {
        // Uncut all
        pieceIds.forEach(id => next.delete(id));
      } else {
        // Cut all
        pieceIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Reset all cut marks
  const resetCutMarks = () => {
    setCutPieceIds(new Set());
  };

  const getUnitLabel = () => {
    if (currentUnits.system === UNIT_SYSTEMS.IMPERIAL) {
      return currentUnits.unit === 'inches' ? 'in' : 'ft';
    }
    return currentUnits.unit;
  };

  // TODO: Guided cut mode is disabled for now. See git history for mode selection and guided mode code.

  // Shop Sheet View
  const numToLetter = (n) => {
    let s = '';
    while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
    return s;
  };

  const grainLabels = {
    horizontal: '↔ Length',
    vertical: '↕ Width',
    none: '○ None',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: darkMode ? '#1a1a1a' : '#F4F7F5' }}>
      {/* Controls Header */}
      <div className="sticky top-0 px-4 py-3 flex items-center justify-end z-10" style={{ backgroundColor: darkMode ? '#111' : '#E8EFEB' }}>
          <div className="flex items-center gap-2">
            {/* Color / Print Toggle */}
            <div className="flex items-center bg-white/60 rounded-full p-0.5">
              <button
                onClick={() => setPrintFriendly(false)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                  !printFriendly ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'
                }`}
              >
                <Palette className="w-3 h-3" />
                Color
              </button>
              <button
                onClick={() => setPrintFriendly(true)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                  printFriendly ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'
                }`}
              >
                <Printer className="w-3 h-3" />
                Print
              </button>
            </div>
            {/* Orientation Toggle - hidden, auto mode is default
            <div className="flex items-center bg-white/60 rounded-full p-0.5">
              <button
                onClick={() => setOrientation('auto')}
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                  orientation === 'auto' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'
                }`}
              >
                Auto
              </button>
              <button
                onClick={() => setOrientation('landscape')}
                className={`p-1 rounded-full transition-all ${
                  orientation === 'landscape' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'
                }`}
              >
                <ArrowLeftRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => setOrientation('portrait')}
                className={`p-1 rounded-full transition-all ${
                  orientation === 'portrait' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'
                }`}
              >
                <ArrowUpDown className="w-3 h-3" />
              </button>
            </div>
            */}
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`relative w-9 h-5 rounded-full transition-colors ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-all ${darkMode ? 'left-[18px]' : 'left-0.5'}`} />
            </button>
            {/* Keep On */}
            {isSupported && (
              <button
                onClick={toggleWakeLock}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                  isLocked ? 'bg-yellow-400 text-gray-900' : 'bg-white/60 text-gray-500'
                }`}
              >
                <Sun className="w-3 h-3" />
                {isLocked ? 'On' : 'Keep'}
              </button>
            )}
            {/* Reset cut marks */}
            {cutPieceIds.size > 0 && (
              <button
                onClick={resetCutMarks}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/60 text-gray-500 hover:bg-white"
                title="Reset all cut marks"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
            {/* PDF Export */}
            <button
              onClick={async () => {
                if (pdfExportStatus === 'generating' || pdfExportStatus === 'downloading') return;
                const result = await downloadShopSheetPDF(
                  {
                    orderedLayouts: exportLayoutsRef.current,
                    currentUnits,
                    sheetWidth,
                    sheetLength,
                    cutWidth,
                    getUnitLabel,
                    orientation,
                  },
                  license?.key,
                  setPdfExportStatus
                );
                if (!result.success && result.checkoutUrl) {
                  promptUpgrade('PDF export is a Pro feature.');
                }
                setTimeout(() => setPdfExportStatus(null), 2000);
              }}
              disabled={pdfExportStatus === 'generating' || pdfExportStatus === 'downloading'}
              className="flex items-center justify-center w-7 h-7 bg-accent-400 hover:bg-accent-500 text-white rounded-lg disabled:opacity-60"
              title="Export as PDF"
            >
              {pdfExportStatus === 'generating' || pdfExportStatus === 'downloading' ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
            </button>
            {/* Print */}
            <button
              onClick={() => printShopSheet({
                orderedLayouts: exportLayoutsRef.current,
                currentUnits,
                sheetWidth,
                sheetLength,
                cutWidth,
                getUnitLabel,
                showWatermark,
                printFriendly,
                orientation,
              })}
              className="flex items-center justify-center w-7 h-7 bg-white border border-gray-300 text-gray-700 rounded-lg"
              title="Print shop sheet"
            >
              <Printer className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Shop Sheets */}
        <div className="p-4 space-y-6">
          {orderedLayouts.map((layout, sheetIndex) => {
            const pieces = layout.cutSequence.filter(cut => cut.type === 'piece');

            // Group pieces by name+dims+grain, tracking all piece IDs in each group
            const groupedParts = [];
            const partMap = new Map();
            pieces.forEach(cut => {
              const p = cut.piece;
              const key = `${p.name}|${p.width.toFixed(4)}|${p.length.toFixed(4)}|${p.grainDirection}`;
              if (!partMap.has(key)) {
                const entry = { num: groupedParts.length + 1, piece: p, qty: 0, pieceIds: [] };
                partMap.set(key, entry);
                groupedParts.push(entry);
              }
              const entry = partMap.get(key);
              entry.qty++;
              if (p.id) {
                entry.pieceIds.push(p.id);
              }
            });

            return (
              <div key={layout.id} className={`rounded-lg shadow-sm flex flex-col ${darkMode ? 'bg-[#242424] border border-[#333]' : 'bg-white border border-gray-200'}`}>
                {/* Header */}
                <div className={`flex justify-between items-baseline px-4 py-2.5 border-b ${darkMode ? 'border-[#333]' : 'border-gray-200'}`}>
                  <h2 className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sheet {sheetIndex + 1} <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>of {orderedLayouts.length}</span>
                  </h2>
                  <span className={`text-[10px] font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {layout.thicknessLabel ? layout.thicknessLabel + ' · ' : ''}
                    {sheetWidth.toFixed(1)} × {sheetLength.toFixed(1)} {getUnitLabel()}
                  </span>
                </div>

                {/* SVG */}
                <div className="shop-sheet-svg-container flex items-center justify-center p-4">
                  <SheetRenderer
                    layout={layout}
                    mode="interactive"
                    highContrast={printFriendly}
                    darkMode={darkMode}
                    maxWidth={340}
                    showLabels={true}
                    showDimensions={true}
                    numberLabels={true}
                    cutSequence={layout.cutSequence}
                    cutPieceIds={cutPieceIds}
                    onPieceClick={(piece) => togglePieceCut(piece.id)}
                  />
                </div>

                {/* Table */}
                <div className="px-4 pb-3">
                  <table className="w-full text-left">
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-[#444]' : 'border-gray-300'}`}>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider w-7 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}></th>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Part</th>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider w-8 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Qty</th>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider w-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>W</th>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider w-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>L</th>
                        <th className={`px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider w-16 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Grain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedParts.map((part, idx) => {
                        const grain = grainLabels[part.piece.grainDirection] || grainLabels.none;
                        const pieceColor = !printFriendly ? getPartColor(part.piece.originalId || part.piece.id).hex : null;
                        const partKey = `${part.piece.name}|${part.piece.width.toFixed(4)}|${part.piece.length.toFixed(4)}|${part.piece.grainDirection}`;
                        const allCut = part.pieceIds.length > 0 && part.pieceIds.every(id => cutPieceIds.has(id));
                        const someCut = part.pieceIds.some(id => cutPieceIds.has(id));
                        const cutCount = part.pieceIds.filter(id => cutPieceIds.has(id)).length;
                        return (
                          <tr
                            key={idx}
                            onClick={() => togglePartGroupCut(partKey, part.pieceIds)}
                            className={`cursor-pointer active:bg-gray-200 transition-colors ${idx % 2 === 0 ? (darkMode ? 'bg-transparent' : 'bg-white') : (darkMode ? 'bg-white/5' : 'bg-gray-50/50')} ${allCut ? 'opacity-50' : ''}`}
                          >
                            <td className={`px-1.5 py-1 text-[11px] font-medium border-b ${darkMode ? 'border-[#333]' : 'border-gray-100'}`}>
                              <span
                                className={`inline-flex items-center justify-center w-5 h-4 rounded-full text-[9px] font-bold ${allCut ? 'line-through' : ''}`}
                                style={pieceColor
                                  ? { backgroundColor: `${pieceColor}30`, color: pieceColor, border: `1.5px solid ${pieceColor}` }
                                  : { backgroundColor: 'transparent', color: darkMode ? '#ccc' : '#333', border: `1.5px solid ${darkMode ? '#ccc' : '#333'}` }
                                }
                              >
                                {numToLetter(part.num)}
                              </span>
                            </td>
                            <td className={`px-1.5 py-1 text-[11px] font-semibold border-b truncate max-w-0 ${darkMode ? 'text-gray-200 border-[#333]' : 'text-gray-800 border-gray-100'} ${allCut ? 'line-through' : ''}`}>
                              {part.piece.name || 'Piece'}
                            </td>
                            <td className={`px-1.5 py-1 text-[11px] font-medium border-b ${darkMode ? 'text-gray-400 border-[#333]' : 'text-gray-600 border-gray-100'}`}>
                              {someCut && !allCut ? (
                                <span>{cutCount}<span className="text-gray-400">/{part.qty}</span></span>
                              ) : (
                                <span className={allCut ? 'line-through' : ''}>×{part.qty}</span>
                              )}
                            </td>
                            <td className={`px-1.5 py-1 text-[11px] font-mono border-b ${darkMode ? 'text-gray-300 border-[#333]' : 'text-gray-700 border-gray-100'} ${allCut ? 'line-through' : ''}`} title={formatPreciseDimension(part.piece.width, currentUnits.unit, currentUnits.system)}>
                              {part.piece.width.toFixed(2)}
                            </td>
                            <td className={`px-1.5 py-1 text-[11px] font-mono border-b ${darkMode ? 'text-gray-300 border-[#333]' : 'text-gray-700 border-gray-100'} ${allCut ? 'line-through' : ''}`} title={formatPreciseDimension(part.piece.length, currentUnits.unit, currentUnits.system)}>
                              {part.piece.length.toFixed(2)}
                            </td>
                            <td className={`px-1.5 py-1 text-[11px] border-b whitespace-nowrap ${darkMode ? 'text-gray-400 border-[#333]' : 'text-gray-500 border-gray-100'} ${allCut ? 'line-through' : ''}`}>
                              {grain}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          {showWatermark ? (
            <div className="space-y-1">
              <div className={`text-base font-semibold ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>FREE VERSION — CUTCALC.COM</div>
              <div className={`text-xs ${darkMode ? 'text-gray-700' : 'text-gray-400'}`}>Upgrade to Pro to remove watermark</div>
            </div>
          ) : (
            <div className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Generated by CutCalc • {new Date().toLocaleDateString()}</div>
          )}
        </div>

        {/* Hidden export-ready SVGs with desktop-appropriate sizing for PDF export */}
        {/* Uses snapshot layouts - never affected by interactive cut marks */}
        <div className="shop-sheet-export-container" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {(exportLayoutsRef.current || orderedLayouts).map((layout) => (
            <div key={`export-${layout.id}`}>
              <SheetRenderer
                layout={layout}
                mode="print"
                highContrast={printFriendly}
                darkMode={false}
                maxWidth={700}
                showLabels={true}
                showDimensions={true}
                numberLabels={true}
                cutSequence={layout.cutSequence}
              />
            </div>
          ))}
        </div>
      </div>
    );
};

export default ExecutionStep;
